import i18n from "i18n";

// export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
export const ACCOUNTING_SYSTEM_API_BASE_URL = process.env.REACT_APP_ACCOUNTING_SYSTEM_API_BASE_URL
export const REVREX_APP_BASE_URL = process.env.REACT_APP_REVREX_APP_BASE_URL;

export const LANGUAGE_KEY = 'i18nextLng';

// GL account-number band each QBO account type is expected to map into. Shared by
// the COA classification screen and the import validation report so both flag the
// same mappings — a type absent here has no asserted band and is never flagged.
export const QBO_ACCOUNT_TYPE_WARNING_RANGES: Record<string, [number, number]> = {
    bank: [10000, 19990],
    cash: [10000, 19990],
    income: [60000, 69990],
    costofgoodssold: [70000, 79980],
    expense: [80000, 89980],
    otherexpense: [89000, 89950],
    otherincome: [99810, 99990],
};


export const normalizeQBOAccountType = (value: string | null | undefined = '') => (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Mirrors gl-api classification_map: several grand headers break OUT of their
// AccountType's band, so AccountType alone is not enough. An AccountType=Expense row
// with AccountSubType=Depreciation is parented to DEPRECIATION(99410-99650), not to
// SG&A (80000-89980) — judging it by the Expense band reports a mismatch on a mapping
// that is actually correct. Resolution order matches the server: name, then subtype,
// then the AccountType band.
export const QBO_GRAND_HEADER_RANGES: Record<string, [number, number]> = {
    interest_net: [90000, 99280],
    taxes_paid: [99290, 99400],
    depreciation: [99410, 99650],
    amortization: [99690, 99800],
    revenue_adjustments: [69800, 69990],
    other_income: [99810, 99990],
    gain_loss_on_sale_of_assets: [99910, 99990],
};

export const QBO_ACCOUNT_SUBTYPE_OVERRIDE: Record<string, string> = {
    interestpaid: 'interest_net',
    taxespaid: 'taxes_paid',
    depreciation: 'depreciation',
    amortization: 'amortization',
    discountsrefundsgiven: 'revenue_adjustments',
    dividendincome: 'other_income',
    interestearned: 'other_income',
    othermiscellaneousincome: 'other_income',
    gainlossonsaleoffixedassets: 'gain_loss_on_sale_of_assets',
};

export const QBO_NAME_OVERRIDE: Record<string, string> = {
    taxespaid: 'taxes_paid',
    interest: 'interest_net',
    depreciation: 'depreciation',
    amortization: 'amortization',
    'gain/lossform4797': 'gain_loss_on_sale_of_assets',
};

// Matches the server's _normalize (lowercase, drop spaces and underscores only) —
// notably it keeps '/', which the NAME_OVERRIDE key 'gain/lossform4797' depends on.
export const normalizeQBOName = (value: string = '') => value.toLowerCase().replace(/[\s_]/g, '');

/**
 * Topmost QBO ancestor of a row, or the row itself when it has no parent.
 *
 * A sub-account's own fields do not identify its section: QBO files children under a
 * generic catch-all AccountSubType ("OtherMiscellaneousServiceCost") and names them
 * with a leaf label ("273 Chance Drive"). gl-api numbers them from the PARENT's range
 * (_allocation_bounds), so the ancestor is what a mapped number must be judged against.
 */
export const rootQBOAncestor = (account: any, accountsById: Map<string, any>): any => {
    let current = account;
    const seen = new Set<string>();
    while (current?.ParentRef_value) {
        const parentKey = String(current.ParentRef_value);
        if (seen.has(parentKey)) break; // defensive: malformed parent cycle
        seen.add(parentKey);
        const parent = accountsById.get(parentKey);
        if (!parent || parent === current) break;
        current = parent;
    }
    return current;
};

export type QBOClassificationMap = {
    by_account_type?: Record<string, { anchors?: number[]; posting_range?: [number, number] }>;
    by_account_sub_type?: Record<string, number[]>;
    by_account_sub_type_range?: Record<string, [number, number]>;
    by_name_range?: Record<string, [number, number]>;
    by_classification?: Record<string, [number, number]>;
};

const asRange = (value?: number[] | null): [number, number] | null =>
    Array.isArray(value) && value.length === 2 ? [Number(value[0]), Number(value[1])] : null;

/**
 * Account-number band a QBO row is expected to map into, or null when nothing is
 * asserted for it. Name and subtype overrides win over the AccountType band, exactly
 * as they do server-side when the account is parented.
 *
 * Pass the /qbo/classification-map response as `serverMap` — it is authoritative, and
 * the tables above are only a fallback for callers that have not loaded it (or for a
 * gl-api too old to serve the *_range keys). Keeping the server first is the point:
 * the frontend's own copy of these bands has drifted twice already.
 */
export const expectedGLRangeForQBOAccount = (
    accountType: string = '',
    accountSubType: string = '',
    accountName: string = '',
    serverMap?: QBOClassificationMap | null,
): [number, number] | null => {
    const nameKey = normalizeQBOName(accountName);
    const subTypeKey = normalizeQBOAccountType(accountSubType);

    const fromServerOverride =
        asRange(serverMap?.by_name_range?.[nameKey]) ||
        asRange(serverMap?.by_account_sub_type_range?.[subTypeKey]);
    if (fromServerOverride) return fromServerOverride;

    const grandHeader = QBO_NAME_OVERRIDE[nameKey] || QBO_ACCOUNT_SUBTYPE_OVERRIDE[subTypeKey];
    if (grandHeader) return QBO_GRAND_HEADER_RANGES[grandHeader] || null;

    const typeKey = normalizeQBOAccountType(accountType);
    return asRange(serverMap?.by_account_type?.[typeKey]?.posting_range)
        || QBO_ACCOUNT_TYPE_WARNING_RANGES[typeKey]
        || null;
};


export const URLS = {
    HOME: '/',
    LOGIN: '/login',
    DASHBOARD: '/dashboard',
    BANK_TRANSACTION: '/bank-transactions',
    USERPROFILE: '/userprofile',
    SETTINGS: '/settings',
    BALANCESHEET: '/balance-sheet',
    CHARTACCOUNT: '/chart-of-accounts',
    ERROR_PAGE: '/error-page',
    USER_VERIFICATION: 'sso/user-verification',
    EXPENSE_GL_CLASSIFICATION: '/expense-gl-classification/:month/:year',
    EXPENSE_GL_CLASSIFICATION_STATUS: '/expense-gl-classification-status',
    REVENUE_GL_CLASSIFICATION: '/revenue-gl-classification/:month/:year',
    REVENUE_GL_CLASSIFICATION_STATUS: '/revenue-gl-classification-status',
    CREDIT_CARD_GL_CLASSIFICATION_STATUS: '/credit-card-gl-classification-status',
    CREDIT_CARD_GL_CLASSIFICATION: '/credit-card-gl-classification/:id',
    BANK_RECONCILE: '/bank-reconcile',
    BANK_RECONCILE_TRANSACTIONS: '/bank-reconcile-transactions/:coa',
    DEPOSIT_GL_CLASSIFICATION: '/deposit-gl-classification/:month/:year',
    DEPOSIT_GL_CLASSIFICATION_STATUS: '/deposit-gl-classification-status',
    BANK_FEED: '/bank-feed',
    SALES_TRANSACTION: '/sales-transactions',
    VIEW_REGISTER: '/view-register',
    PROFITLOSS: '/profit-loss',
    FOURTHREETHREEB: '/four-three-three-b',
    BUDGET: '/budget',
    BUDGET_VS_ACTUAL: '/budget-vs-actual',
    STATEMENT_OF_CASH_FLOW: '/statement-of-cash-flow',
    BUSINESS_RATIO: '/business-ratios',
    SALES: '/sales',
    PERSONAL_BALANCE_SHEET: '/personal-balance-sheet',
    PERSONAL_INCOME_STATMENT: '/personal-income-statement',
    TAX_RESOLUTION_FORMS: '/tax-resolution-forms',
    TAX_FORM_433B:'/tax-resolution-forms/f433b',
    OPENNING_BALANCE: '/opening-balance',
    THIRD_PARTY: '/third-party-list',
    THIRD_PARTY_CONNECTION: '/third-party-connection',
    QUICKBOOK_CALLBACK: '/quickbook-callback',
    QUICKBOOK_DASHBOARD: '/quickbook-dashboard',
    QUICKBOOK_COA: '/quickbook-coa',
    QUICKBOOK_BB: '/quickbook-bb',
    QUICKBOOK_BS_PL: '/quickbook-bs-pl',
    QUICKBOOK_BS: '/quickbook-bs/:month/:year',
    QUICKBOOK_PL: '/quickbook-pl/:month/:year',
    TAX_1099_PORTFOLIO: '/1099-portfolio',
    TAX_1099_VENDORS: '/1099-vendors',
    VENDOR_CENTER: '/vendor-center',
    TAX_1099_PAYEE_REVIEW: '/1099-payee-review',
    TAX_1099_PAYMENTS: '/1099-payments',
    TAX_1099_EXPENSE_SYNC: '/1099-expense-sync',
    TAX_1099_FORMS: '/1099-forms',
    TAX_1099_YEAR_END_REVIEW: '/1099-year-end-review',
    TAX_1099_PAYER_SETTINGS: '/1099-payer-settings',
    QBO_VENDORS: '/qbo-vendors',
    QBO_CUSTOMERS: '/qbo-customers',
    QBO_BACKUP_STATUS_PAGE: '/qbo-backup-status/:jobId',
    PAGE_NOT_FOUND:'/page-not-found'
}

export const API_URLS = {
    USER: {
        // LOGIN: `${API_BASE_URL}/auth/sign_in`,
        // LOGOUT: `${API_BASE_URL}/users/logout`,
        DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/auth/me`,
        USER_ACCESS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/auth/get-user-token`,
        // ACCOUNT_ALLOWANCE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/mapped/records`,
        // USER_STEP_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/step/statuses`
    },
    CHART_ACCOUNT : {
        LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
        DETAILS : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
        CREATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
        UPDATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
        AVAILABLE_ACCOUNT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/available/accounts`,
        GL_ACCOUNT_LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/vplist`,
        LIST_TREE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/openapi/accounts`,
        ACCOUNT_LEBEL : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/get/label`,
        BANK_SALE_LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/revrex/bank-sales-list`,
        BANK_SALE_UPDATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/revrex/bank-sales-list`,
        FETCH_OPENNING_BALANCE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/beginning/balances`,
        POST_OPENNING_BALANCE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/beginning/balances`,
        DELETE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
    },
    GL_CLASSIFICATION : {
        GL_TRANS_LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts`,
        GL_RECONCILE_TRANS_LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations`,
        GL_SALES_LIST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/sales/classifications`,
        BANK_GL_UPDATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/expenses`,
        SALE_GL_UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/sales/activities`,
        REVENUE_GL_UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/deposits`,
        CLASSIFICATION_STATUS : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/completion/statuses`,
        COMPLETE_STEP : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/complete/steps`,
        GL_RECONCILE_TRANS_POST : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations`,
        GL_RECONCILE_OPENNING_BALANCE_FETCH : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/opening/balances`,
        SKIP_BEGINNING_BALANCE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/skip/beginning/balances`,
        GL_BANK_RECONCILE_BALANCES: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/balances`,
       
        GL_RECONCILE_QBD_BACKFILL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/qbd-backfill`,
    },
    BANK_FEED: {
        FETCH_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/bank/feeds`,
        UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/bank/feeds`,
        BULK_UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/bank/bulkfeeds`,
        FETCH_BANK_TEMPLATES: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/bank-template-list`,
        ADD_BANK_ACCOUNT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/create-bank-account`,
        REQUEST_BANK: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/bank/reconciliations/bank-requests`,
    },
    VIEW_REGISTER: {
        FETCH_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/view/registers`,
        EXPORT_CSV: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/generate-csv-report`
    },
    REPORT: {
        BALANCE_SHEET: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets`,
        REFRESH_BALANCE_SHEET: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/refresh/balance-sheet`,
        REGENERATE_BALANCE_SHEET: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/regenerate`,
        BALANCE_SHEET_REFRESH_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/refresh-status`,
        PROFIT_LOSSS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/pnls`,
        STATEMENT_OF_CASH_FLOW: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/cash/flows`,
        DASHBOARD_RATIO: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/ratios`,
        REPORT_DOWNLOAD_BS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/generate/reports`,
        REPORT_DOWNLOAD_BT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/bank-sales/generate/reports`,
        TXN_REGISTER_PL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/pnl-listing`,
    },
    TRANSACTION: {
        TRANS_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/bank-sales`,
        CREATE_TRANSACTION_ADJUSTMENT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/adjustments`,
        DISCARD_TRANSACTION: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/credit/card/transactions`
    },
    FOUR_THREE_THREE_B : {
        DETAILS : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/business/statements/details`,
        CREATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/business/statements/business_statements`,
        UPDATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts`,
        MONTHLY_EXPENSE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/balance/sheets/form/433/populates`,
    },
    TAX_1099: {
        UNMAPPED_PAYEES: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/expenses/unmapped-payees`,
        RESOLVE_PAYEE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/expenses/resolve-payee`,
        STATEMENT_EVIDENCE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/expenses/statement-evidence`,
        VENDOR_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        VENDOR_CREATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        VENDOR_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        VENDOR_UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        VENDOR_DELETE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        PAYER_SETTINGS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/settings`,
        PAYMENT_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments`,
        PAYMENT_CREATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments`,
        PAYMENT_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments`,
        PAYMENT_UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments`,
        PAYMENT_DELETE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments`,
        PAYMENT_SUMMARY: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments/summary`,
        FORM_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_GENERATE_BATCH: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms/generate`,
        FORM_GENERATE_VENDOR: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        FORM_PDF: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_APPROVE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_UNAPPROVE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_VOID: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_FURNISH_EMAIL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        FORM_CORRECT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/forms`,
        VENDOR_SYNC_QBO: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors/sync-qbo`,
        PAYMENT_SYNC_EXPENSES: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/payments/sync-expenses`,
        VENDOR_PAYMENTS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        W9_PDF: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors`,
        PAYER_W9_UPLOAD: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/settings/w9`,
        PAYER_W9_PDF: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/settings/w9/pdf`,
        VENDOR_NEXT_CODE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/vendors/next-code`,
        YEAR_END_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/year-end/status`,
        YEAR_END_APPROVE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_1099/year-end/approve`,
    },
    TAX_RESOLUTION:{
        DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_resolution/details`,
        CREATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_resolution/save`,
        PUBLISH: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_resolution/publish`,
        PNL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_resolution/pnls`,
        DRAFT_LIST:`${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/tax_resolution/drafts`
    },
    PERSONAL_BALANCE_SHEET: {
        DETAILS : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/personal/balance/sheets/details`,
        CREATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/personal/balance/sheets`,
    },
    PERSONAL_INCOME_STATMENT: {
        DETAILS : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/personal/income/statements/details`,
        CREATE : `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/personal/income/statements`,
    },
    SETTINGS: {
        // FETCH_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/settings/details`,
        UPDATE_ACCOUNTING: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/settings`,
        UPDATE_SALES: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/account/charts/primary/sales`,
        FETCH_ACCOUNT_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/auth/user/account/settings`,
        UPGRADE_TO_LATEST_GL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/remove/opening-balances`,
    },
    BUDGET: {
        FETCH_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/all`,
        ADD: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets`,
        UPDATE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/update`,
        PUBLISH: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/publish`,
        DOWNLOAD_REPORT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/generate/reports`,
        DOWNLOAD_REPORT_EXCEL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/generate/reports`,
        BUDGET_VS_ACTUAL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/vs-actual`,
        BUDGET_VS_ACTUAL_REPORT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/budgets/vs-actual/generate/reports`,
    },
    CREDIT_CARD: {
        FETCH_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/credit-card-uploads-listing`,
        FETCH_DETAILS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/credit-cards-listing`,
        UPLOAD_CSV: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/upload/csv/fetch-columns`,
        CC_UPLOAD: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/upload/csv/credit-cards`,
        CC_DELETE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/credit-cards`,
        YEAR_LOCK_CHECK: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/year/lock-check`,
        LOCKED_YEARS_LIST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/transaction/accounts/year/locked-years-list`,
    },
    QUICK_BOOK: {
        AUTH: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/auth/url`,
        CALLBACK: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/callback`,
        IMPORT_DATA: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/import/data`,
        FETCH_ACCOUNT_CHARTS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/account/charts`,
        POST_ACCOUNT_CHARTS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/account/charts`,
        CLASSIFICATION_MAP: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/classification-map`,
        PARENT_MATCH: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/parent-match`,
        QB_FETCH_OPENNING_BALANCE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/begining/balance/begining/balance`,
        QB_POST_OPENNING_BALANCE: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/begining/balance/beginning/balances`,
        QB_FETCH_BS_PL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/bs/pl`,
        QB_POST_BS_PL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/submit/bspl`,
        QB_FETCH_BS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/balance/sheets`,
        QB_FETCH_PL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/profit/loss`,
        QB_FETCH_VENDORS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/vendors`,
        QB_FETCH_CUSTOMERS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/customers`,
        QB_JOB_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/job/status`,
        QB_ACKNOWLEDGE_JOB_WARNINGS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/balance/sheets/job/acknowledge`,
        QB_IMPORT_JOB_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/import/status`,
        QB_LEDGER_AUDIT: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/ledger/audit`,
        QB_LEDGER_RETRY: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/ledger/retry`,
        QB_LEDGER_RETRY_ALL: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/ledger/retry-all`,
        QB_LEDGER_MAPPINGS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/ledger/mappings`,
        QB_STEP_BACK: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/step/back`,
        QBO_BACKUP_LATEST: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/backup/latest`,
        QBO_BACKUP_STATUS: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/backup/status`,
        QBO_BACKUP_RETRY: `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/backup/retry`,
    },
}

export const SAGA_ACTIONS = {
    USER: {
        LOGIN: 'SAGA_ACTIONS.USER.LOGIN',
        LOGOUT: 'SAGA_ACTIONS.USER.LOGOUT',
        DETAILS: 'SAGA_ACTIONS.USER.DETAILS',
        USER_ACCESS: 'SAGA_ACTIONS.USER.USER_ACCESS',
        // ACCOUNT_ALLOWANCE: 'SAGA_ACTIONS.USER.ACCOUNT_ALLOWANCE',
        // USER_STEP_STATUS: 'SAGA_ACTIONS.USER.USER_STEP_STATUS',
    },
    CHART_ACCOUNT : {
        LIST : 'SAGA_ACTIONS.CHART_ACCOUNT.LIST',
        DETAILS : 'SAGA_ACTIONS.CHART_ACCOUNT.DETAILS',
        CREATE : 'SAGA_ACTIONS.CHART_ACCOUNT.CREATE',
        UPDATE : 'SAGA_ACTIONS.CHART_ACCOUNT.UPDATE',
        AVAILABLE_ACCOUNT : 'SAGA_ACTIONS.CHART_ACCOUNT.AVAILABLE_ACCOUNT',
        GL_ACCOUNT_LIST : 'SAGA_ACTIONS.CHART_ACCOUNT.GL_ACCOUNT_LIST',
        LIST_TREE : 'SAGA_ACTIONS.CHART_ACCOUNT.GL_ACCOUNT_LIST_TREE',
        ACCOUNT_LEBEL : 'SAGA_ACTIONS.CHART_ACCOUNT.ACCOUNT_LEBEL',
        BANK_SALE_LIST : 'SAGA_ACTIONS.CHART_ACCOUNT.BANK_SALE_LIST',
        BANK_SALE_UPDATE : 'SAGA_ACTIONS.CHART_ACCOUNT.BANK_SALE_UPDATE',
        FETCH_OPENNING_BALANCE : 'SAGA_ACTIONS.CHART_ACCOUNT.FETCH_OPENNING_BALANCE',
        POST_OPENNING_BALANCE : 'SAGA_ACTIONS.CHART_ACCOUNT.POST_OPENNING_BALANCE',
        DELETE : 'SAGA_ACTIONS.CHART_ACCOUNT.DELETE',
    },
    GL_CLASSIFICATION : {
        GL_TRANS_LIST : 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_TRANS_LIST',
        GL_RECONCILE_TRANS_LIST : 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_RECONCILE_TRANS_LIST',
        GL_SALES_LIST : 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_SALES_LIST',
        BANK_GL_UPDATE : 'SAGA_ACTIONS.GL_CLASSIFICATION.BANK_GL_UPDATE',
        SALE_GL_UPDATE : 'SAGA_ACTIONS.GL_CLASSIFICATION.SALE_GL_UPDATE',
        REVENUE_GL_UPDATE : 'SAGA_ACTIONS.GL_CLASSIFICATION.REVENUE_GL_UPDATE',
        CLASSIFICATION_STATUS : 'SAGA_ACTIONS.GL_CLASSIFICATION.CLASSIFICATION_STATUS',
        COMPLETE_STEP : 'SAGA_ACTIONS.GL_CLASSIFICATION.COMPLETE_STEP',
        GL_RECONCILE_TRANS_POST : 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_RECONCILE_TRANS_POST',
        GL_RECONCILE_OPENNING_BALANCE_FETCH : 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_RECONCILE_OPENNING_BALANCE_FETCH',
        SKIP_BEGINNING_BALANCE: 'SAGA_ACTIONS.GL_CLASSIFICATION.SKIP_BEGINNING_BALANCE',
        GL_BANK_RECONCILE_BALANCES: 'SAGA_ACTIONS.GL_CLASSIFICATION.GET_BANK_RECONCILE_BALANCES',
        GL_RECONCILE_QBD_BACKFILL: 'SAGA_ACTIONS.GL_CLASSIFICATION.GL_RECONCILE_QBD_BACKFILL',
    },
    BANK_FEED: {
        FETCH_LIST: 'SAGA_ACTIONS.BANK_FEED.FETCH_LIST',
        UPDATE: 'SAGA_ACTIONS.BANK_FEED.UPDATE',
        BULK_UPDATE: 'SAGA_ACTIONS.BANK_FEED.BULK_UPDATE',
        FETCH_BANK_TEMPLATES: 'SAGA_ACTIONS.BANK_FEED.FETCH_BANK_TEMPLATES',
        ADD_BANK_ACCOUNT: 'SAGA_ACTIONS.BANK_FEED.ADD_BANK_ACCOUNT',
        ADD_BANK_TEMPLATE: 'SAGA_ACTIONS.BANK_FEED.ADD_BANK_TEMPLATE',
        REQUEST_BANK: 'SAGA_ACTIONS.BANK_FEED.REQUEST_BANK',
    },
    VIEW_REGISTER: {
        FETCH_LIST: 'SAGA_ACTIONS.VIEW_REGISTER.FETCH_LIST',
        EXPORT_CSV: 'SAGA_ACTIONS.BANK_FEED.EXPORT_CSV'
    },
    REPORT: {
        BALANCE_SHEET: 'SAGA_ACTIONS.REPORT.BALANCE_SHEET',
        REFRESH_BALANCE_SHEET: 'SAGA_ACTIONS.REPORT.REFRESH_BALANCE_SHEET',
        REGENERATE_BALANCE_SHEET: 'SAGA_ACTIONS.REPORT.REGENERATE_BALANCE_SHEET',
        BALANCE_SHEET_REFRESH_STATUS: 'SAGA_ACTIONS.REPORT.BALANCE_SHEET_REFRESH_STATUS',
        PROFIT_LOSSS: 'SAGA_ACTIONS.REPORT.PROFIT_LOSSS',
        STATEMENT_OF_CASH_FLOW: 'SAGA_ACTIONS.REPORT.STATEMENT_OF_CASH_FLOW',
        DASHBOARD_RATIO: 'SAGA_ACTIONS.REPORT.DASHBOARD_RATIO',
        REPORT_DOWNLOAD_BS: 'SAGA_ACTIONS.REPORT.REPORT_DOWNLOAD_BS',
        REPORT_DOWNLOAD_BT: 'SAGA_ACTIONS.REPORT.REPORT_DOWNLOAD_BT',
        TXN_REGISTER_PL: 'SAGA_ACTIONS.REPORT.TXN_REGISTER_PL',
    },
    TRANSACTION: {
        TRANS_LIST: 'SAGA_ACTIONS.TRANSACTION.TRANS_LIST',
        CREATE_TRANSACTION_ADJUSTMENT: 'SAGA_ACTIONS.TRANSACTION.CREATE_TRANSACTION_ADJUSTMENT',
        DISCARD_TRANSACTION: 'SAGA_ACTIONS.TRANSACTION.DISCARD_TRANSACTION',
    },
    FOUR_THREE_THREE_B : {
        DETAILS : 'SAGA_ACTIONS.FOUR_THREE_THREE_B.DETAILS',
        CREATE : 'SAGA_ACTIONS.FOUR_THREE_THREE_B.CREATE',
        UPDATE : 'SAGA_ACTIONS.FOUR_THREE_THREE_B.UPDATE',
        MONTHLY_EXPENSE : 'SAGA_ACTIONS.FOUR_THREE_THREE_B.MONTHLY_EXPENSE',
    },
    TAX_1099 : {
        UNMAPPED_PAYEES : 'SAGA_ACTIONS.TAX_1099.UNMAPPED_PAYEES',
        RESOLVE_PAYEE : 'SAGA_ACTIONS.TAX_1099.RESOLVE_PAYEE',
        STATEMENT_EVIDENCE : 'SAGA_ACTIONS.TAX_1099.STATEMENT_EVIDENCE',
        VENDOR_LIST : 'SAGA_ACTIONS.TAX_1099.VENDOR_LIST',
        VENDOR_CREATE : 'SAGA_ACTIONS.TAX_1099.VENDOR_CREATE',
        VENDOR_DETAILS : 'SAGA_ACTIONS.TAX_1099.VENDOR_DETAILS',
        VENDOR_UPDATE : 'SAGA_ACTIONS.TAX_1099.VENDOR_UPDATE',
        VENDOR_DELETE : 'SAGA_ACTIONS.TAX_1099.VENDOR_DELETE',
        PAYER_SETTINGS_GET : 'SAGA_ACTIONS.TAX_1099.PAYER_SETTINGS_GET',
        PAYER_SETTINGS_SAVE : 'SAGA_ACTIONS.TAX_1099.PAYER_SETTINGS_SAVE',
        PAYMENT_LIST : 'SAGA_ACTIONS.TAX_1099.PAYMENT_LIST',
        PAYMENT_CREATE : 'SAGA_ACTIONS.TAX_1099.PAYMENT_CREATE',
        PAYMENT_DETAILS : 'SAGA_ACTIONS.TAX_1099.PAYMENT_DETAILS',
        PAYMENT_UPDATE : 'SAGA_ACTIONS.TAX_1099.PAYMENT_UPDATE',
        PAYMENT_DELETE : 'SAGA_ACTIONS.TAX_1099.PAYMENT_DELETE',
        PAYMENT_SUMMARY : 'SAGA_ACTIONS.TAX_1099.PAYMENT_SUMMARY',
        FORM_LIST : 'SAGA_ACTIONS.TAX_1099.FORM_LIST',
        FORM_DETAILS : 'SAGA_ACTIONS.TAX_1099.FORM_DETAILS',
        FORM_GENERATE_BATCH : 'SAGA_ACTIONS.TAX_1099.FORM_GENERATE_BATCH',
        FORM_GENERATE_VENDOR : 'SAGA_ACTIONS.TAX_1099.FORM_GENERATE_VENDOR',
        W9_EXTRACT : 'SAGA_ACTIONS.TAX_1099.W9_EXTRACT',
        W9_CONFIRM : 'SAGA_ACTIONS.TAX_1099.W9_CONFIRM',
        W9_CURRENT : 'SAGA_ACTIONS.TAX_1099.W9_CURRENT',
        FORM_PDF : 'SAGA_ACTIONS.TAX_1099.FORM_PDF',
        FORM_APPROVE : 'SAGA_ACTIONS.TAX_1099.FORM_APPROVE',
        FORM_UNAPPROVE : 'SAGA_ACTIONS.TAX_1099.FORM_UNAPPROVE',
        FORM_VOID : 'SAGA_ACTIONS.TAX_1099.FORM_VOID',
        FORM_FURNISH_EMAIL : 'SAGA_ACTIONS.TAX_1099.FORM_FURNISH_EMAIL',
        FORM_CORRECT : 'SAGA_ACTIONS.TAX_1099.FORM_CORRECT',
        VENDOR_SYNC_QBO : 'SAGA_ACTIONS.TAX_1099.VENDOR_SYNC_QBO',
        PAYMENT_SYNC_EXPENSES : 'SAGA_ACTIONS.TAX_1099.PAYMENT_SYNC_EXPENSES',
        VENDOR_PAYMENTS : 'SAGA_ACTIONS.TAX_1099.VENDOR_PAYMENTS',
        W9_PDF : 'SAGA_ACTIONS.TAX_1099.W9_PDF',
        PAYER_W9_UPLOAD : 'SAGA_ACTIONS.TAX_1099.PAYER_W9_UPLOAD',
        YEAR_END_STATUS : 'SAGA_ACTIONS.TAX_1099.YEAR_END_STATUS',
        YEAR_END_APPROVE : 'SAGA_ACTIONS.TAX_1099.YEAR_END_APPROVE',
        PAYER_W9_PDF : 'SAGA_ACTIONS.TAX_1099.PAYER_W9_PDF',
        VENDOR_NEXT_CODE : 'SAGA_ACTIONS.TAX_1099.VENDOR_NEXT_CODE',
    },
    TAX_RESOLUTIONS : {
        DETAILS : 'SAGA_ACTIONS.TAX_RESOLUTION.DETAILS',
        CREATE : 'SAGA_ACTIONS.TAX_RESOLUTION.CREATE',
        PUBLISH : 'SAGA_ACTIONS.TAX_RESOLUTION.PUBLISH',
        PNL : 'SAGA_ACTIONS.TAX_RESOLUTION.PNL',
        DRAFT_LIST:"SAGA_ACTIONS.TAX_RESOLUTION.DRAFT_LIST"
    },
    PERSONAL_BALANCE_SHEET: {
        DETAILS : 'SAGA_ACTIONS.PERSONAL_BALANCE_SHEET.DETAILS',
        CREATE : 'SAGA_ACTIONS.PERSONAL_BALANCE_SHEET.CREATE',
    },
    PERSONAL_INCOME_STATMENT: {
        DETAILS : 'SAGA_ACTIONS.PERSONAL_INCOME_STATMENT.DETAILS',
        CREATE : 'SAGA_ACTIONS.PERSONAL_INCOME_STATMENT.CREATE',
    },
    SETTINGS: {
        // FETCH_DETAILS: 'SAGA_ACTIONS.SETTINGS.FETCH_DETAILS',
        UPDATE_ACCOUNTING: 'SAGA_ACTIONS.SETTINGS.UPDATE_ACCOUNTING',
        UPDATE_SALES: 'SAGA_ACTIONS.SETTINGS.UPDATE_SALES',
        FETCH_ACCOUNT_DETAILS: 'SAGA_ACTIONS.SETTINGS.FETCH_ACCOUNT_DETAILS',
        UPGRADE_TO_LATEST_GL: 'SAGA_ACTIONS.SETTINGS.UPGRADE_TO_LATEST_GL',
    },
    BUDGET: {
        FETCH_DETAILS: 'SAGA_ACTIONS.BUDGET.FETCH_DETAILS',
        ADD: 'SAGA_ACTIONS.BUDGET.ADD',
        UPDATE: 'SAGA_ACTIONS.BUDGET.UPDATE',
        PUBLISH: 'SAGA_ACTIONS.BUDGET.PUBLISH',
        DOWNLOAD_REPORT: 'SAGA_ACTIONS.BUDGET.DOWNLOAD_REPORT',
        DOWNLOAD_REPORT_EXCEL: 'SAGA_ACTIONS.BUDGET.DOWNLOAD_REPORT_EXCEL',
    },
    CREDIT_CARD: {
        FETCH_LIST: 'SAGA_ACTIONS.CREDIT_CARD.FETCH_LIST',
        FETCH_DETAILS: 'SAGA_ACTIONS.CREDIT_CARD.FETCH_DETAILS',
        UPLOAD_CSV: 'SAGA_ACTIONS.CREDIT_CARD.UPLOAD_CSV',
        CC_UPLOAD: 'SAGA_ACTIONS.CREDIT_CARD.CC_UPLOAD',
        CC_DELETE: 'SAGA_ACTIONS.CREDIT_CARD.CC_DELETE',
        YEAR_LOCK_CHECK: 'SAGA_ACTIONS.CREDIT_CARD.YEAR_LOCK_CHECK',
        LOCKED_YEARS_LIST: 'SAGA_ACTIONS.CREDIT_CARD.LOCKED_YEARS_LIST',
    },
    QUICK_BOOK: {
        AUTH: 'SAGA_ACTIONS.QUICK_BOOK.AUTH',
        CALLBACK: 'SAGA_ACTIONS.QUICK_BOOK.CALLBACK',
        IMPORT_DATA: 'SAGA_ACTIONS.QUICK_BOOK.IMPORT_DATA',
        FETCH_ACCOUNT_CHARTS: 'SAGA_ACTIONS.QUICK_BOOK.FETCH_ACCOUNT_CHARTS',
        POST_ACCOUNT_CHARTS: 'SAGA_ACTIONS.QUICK_BOOK.POST_ACCOUNT_CHARTS',
        CLASSIFICATION_MAP: 'SAGA_ACTIONS.QUICK_BOOK.CLASSIFICATION_MAP',
        PARENT_MATCH: 'SAGA_ACTIONS.QUICK_BOOK.PARENT_MATCH',
        QB_FETCH_OPENNING_BALANCE : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_OPENNING_BALANCE',
        QB_POST_OPENNING_BALANCE : 'SAGA_ACTIONS.QUICK_BOOK.QB_POST_OPENNING_BALANCE',
        QB_FETCH_BS_PL : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_BS_PL',
        QB_POST_BS_PL : 'SAGA_ACTIONS.QUICK_BOOK.QB_POST_BS_PL',
        QB_FETCH_BS : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_BS',
        QB_FETCH_PL : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_PL',
        QB_FETCH_VENDORS : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_VENDORS',
        QB_FETCH_CUSTOMERS : 'SAGA_ACTIONS.QUICK_BOOK.QB_FETCH_CUSTOMERS',
        QB_JOB_STATUS : 'SAGA_ACTIONS.QUICK_BOOK.QB_JOB_STATUS',
        QB_ACKNOWLEDGE_JOB_WARNINGS : 'SAGA_ACTIONS.QUICK_BOOK.QB_ACKNOWLEDGE_JOB_WARNINGS',
        QB_IMPORT_JOB_STATUS : 'SAGA_ACTIONS.QUICK_BOOK.QB_IMPORT_JOB_STATUS',
        QB_LEDGER_AUDIT : 'SAGA_ACTIONS.QUICK_BOOK.QB_LEDGER_AUDIT',
        QB_LEDGER_RETRY : 'SAGA_ACTIONS.QUICK_BOOK.QB_LEDGER_RETRY',
        QB_LEDGER_RETRY_ALL : 'SAGA_ACTIONS.QUICK_BOOK.QB_LEDGER_RETRY_ALL',
        QB_LEDGER_MAPPINGS : 'SAGA_ACTIONS.QUICK_BOOK.QB_LEDGER_MAPPINGS',
        QBO_BACKUP_LATEST : 'SAGA_ACTIONS.QUICK_BOOK.QBO_BACKUP_LATEST',
        QBO_BACKUP_STATUS : 'SAGA_ACTIONS.QUICK_BOOK.QBO_BACKUP_STATUS',
        QBO_BACKUP_RETRY : 'SAGA_ACTIONS.QUICK_BOOK.QBO_BACKUP_RETRY',
    },
}

export const CA_REPORT = [{
    label: 'BS',
    value: 'BS'
}, {
    label: 'PL',
    value: 'PL'
}]

export const CA_ACC_TYPE = [{
    label: 'Debit',
    value: 'D'
}, {
    label: 'Credit',
    value: 'C'
}]

export const CC_AMOUNT_TYPE = [{
    label: 'Credit card charge amount negative',
    value: 1
}, {
    label: 'Credit card charge amount positive',
    value: 2
}, {
    label: 'Separate Column To Define Transaction Type',
    value: 3
}]


export const CC_DATE_FORMAT = [{
    label: "MM/DD/YYYY",
    value: "MM/DD/YYYY"
}, {
    label: "DD/MM/YYYY",
    value: "DD/MM/YYYY"
}, {
    label: "MM-DD-YYYY",
    value: "MM-DD-YYYY"
}, {
    label: "DD-MM-YYYY",
    value: "DD-MM-YYYY"
}, {
    label: "MM/DD/YYYY & MM-DD-YYYY",
    value: "MM/DD/YYYY&MM-DD-YYYY"
}, {
    label: "DD/MM/YYYY & DD-MM-YYYY",
    value: "DD/MM/YYYY&DD-MM-YYYY"
}, {
    label: "YYYY/MM/DD",
    value: "YYYY/MM/DD"
}, {
    label: "YYYY-MM-DD",
    value: "YYYY-MM-DD"
}, {
    label: "YYYY/MM/DD & YYYY-MM-DD",
    value: "YYYY/MM/DD&YYYY-MM-DD"
}]

export const MONTHS = [
    { value: '01', label: i18n.t('month.01', { ns: 'resources.form-validator' })},
    { value: '02', label: i18n.t('month.02', { ns: 'resources.form-validator' })},
    { value: '03', label: i18n.t('month.03', { ns: 'resources.form-validator' })},
    { value: '04', label: i18n.t('month.04', { ns: 'resources.form-validator' })},
    { value: '05', label: i18n.t('month.05', { ns: 'resources.form-validator' })},
    { value: '06', label: i18n.t('month.06', { ns: 'resources.form-validator' })},
    { value: '07', label: i18n.t('month.07', { ns: 'resources.form-validator' })},
    { value: '08', label: i18n.t('month.08', { ns: 'resources.form-validator' })},
    { value: '09', label: i18n.t('month.09', { ns: 'resources.form-validator' })},
    { value: '10', label: i18n.t('month.10', { ns: 'resources.form-validator' })},
    { value: '11', label: i18n.t('month.11', { ns: 'resources.form-validator' })},
    { value: '12', label: i18n.t('month.12', { ns: 'resources.form-validator' })},
]

export const BUDGET_BASED = [{
    value: 1, label: 'Calendar Year'
}, {
    value: 2, label: 'Fiscal Year'
}]

export const MENUS = {
    DASHBOARD: ['dashboard'],
    Classification: ['sales-gl-classification', 'revenue-gl-classification', 'expense-gl-classification', 'expense-gl-classification-status', 'deposit-gl-classification-status', 'revenue-gl-classification-status', 'bank-reconcile'],
    CHART_OF_ACCOUNT: ['chart-of-accounts', 'bank-feed', 'view-register', 'budget', 'opening-balance'],
    // BUDGET: ['budget'],
    FORMS: ['four-three-three-b', 'personal-balance-sheet', 'personal-income-statement'],
    REPORTS: ['bank-transactions', 'sales-transactions', 'balance-sheet', 'profit-loss', 'statement-of-cash-flow', 'business-ratios', 'budget-vs-actual'],
    TAX_1099: [
        'vendor-center',
        '1099-payer-settings',
        '1099-vendors',
        '1099-payments',
        '1099-forms',
        '1099-portfolio',
        '1099-expense-sync',
        '1099-year-end-review',
    ],
    TAX_RESOLUTION: ['tax-resolution-forms','tax-form-f433b'],
    // BUSINESS_RATIOS: ['business-ratios'],
}

export const GL_VP = {
    BANK_ACCOUNT: {
        account: [10020]
    },
    SALES_ACTIVITY: {
        account: [60010, 61000, 67000, 69800]
    },
    CREDIT_CARD: {
        account: [40020, 40500, 42000]
    },
    DEBIT_CARD: {
        account: [10020, 10140, 10510, 12000, 19460, 20010, 30000],
        exclude_accounts: [10020, 10140, 11510, 12000, 19460, 20010, 30000, 40020, 40500, 42000, 43000, 50010, 52000]
    },
    EXPENSE: {
        account: [80000]
    },
    EQUITY: {
        account: [50000, 50010, 52000]
    }
}

export interface USStateOption {
    code: string;
    name: string;
}

export const US_STATES: USStateOption[] = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'GU', name: 'Guam' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
];

export const STATES = [
    { country_id: 1, state: 'Alabama', abbreviation: 'AL' },
    { country_id: 1, state: 'Alaska', abbreviation: 'AK' },
    { country_id: 1, state: 'Arizona', abbreviation: 'AZ' },
    { country_id: 1, state: 'Arkansas', abbreviation: 'AR' },
    { country_id: 1, state: 'California', abbreviation: 'CA' },
    { country_id: 1, state: 'Colorado', abbreviation: 'CO' },
    { country_id: 1, state: 'Connecticut', abbreviation: 'CT' },
    { country_id: 1, state: 'Delaware', abbreviation: 'DE' },
    { country_id: 1, state: 'Florida', abbreviation: 'FL' },
    { country_id: 1, state: 'Georgia', abbreviation: 'GA' },
    { country_id: 1, state: 'Hawaii', abbreviation: 'HI' },
    { country_id: 1, state: 'Idaho', abbreviation: 'ID' },
    { country_id: 1, state: 'Illinois', abbreviation: 'IL' },
    { country_id: 1, state: 'Indiana', abbreviation: 'IN' },
    { country_id: 1, state: 'Iowa', abbreviation: 'IA' },
    { country_id: 1, state: 'Kansas', abbreviation: 'KS' },
    { country_id: 1, state: 'Kentucky', abbreviation: 'KY' },
    { country_id: 1, state: 'Louisiana', abbreviation: 'LA' },
    { country_id: 1, state: 'Maine', abbreviation: 'ME' },
    { country_id: 1, state: 'Maryland', abbreviation: 'MD' },
    { country_id: 1, state: 'Massachusetts', abbreviation: 'MA' },
    { country_id: 1, state: 'Michigan', abbreviation: 'MI' },
    { country_id: 1, state: 'Minnesota', abbreviation: 'MN' },
    { country_id: 1, state: 'Mississippi', abbreviation: 'MS' },
    { country_id: 1, state: 'Missouri', abbreviation: 'MO' },
    { country_id: 1, state: 'Montana', abbreviation: 'MT' },
    { country_id: 1, state: 'Nebraska', abbreviation: 'NE' },
    { country_id: 1, state: 'Nevada', abbreviation: 'NV' },
    { country_id: 1, state: 'New Hampshire', abbreviation: 'NH' },
    { country_id: 1, state: 'New Jersey', abbreviation: 'NJ' },
    { country_id: 1, state: 'New Mexico', abbreviation: 'NM' },
    { country_id: 1, state: 'New York', abbreviation: 'NY' },
    { country_id: 1, state: 'North Carolina', abbreviation: 'NC' },
    { country_id: 1, state: 'North Dakota', abbreviation: 'ND' },
    { country_id: 1, state: 'Ohio', abbreviation: 'OH' },
    { country_id: 1, state: 'Oklahoma', abbreviation: 'OK' },
    { country_id: 1, state: 'Oregon', abbreviation: 'OR' },
    { country_id: 1, state: 'Pennsylvania', abbreviation: 'PA' },
    { country_id: 1, state: 'Rhode Island', abbreviation: 'RI' },
    { country_id: 1, state: 'South Carolina', abbreviation: 'SC' },
    { country_id: 1, state: 'South Dakota', abbreviation: 'SD' },
    { country_id: 1, state: 'Tennessee', abbreviation: 'TN' },
    { country_id: 1, state: 'Texas', abbreviation: 'TX' },
    { country_id: 1, state: 'Utah', abbreviation: 'UT' },
    { country_id: 1, state: 'Vermont', abbreviation: 'VT' },
    { country_id: 1, state: 'Virginia', abbreviation: 'VA' },
    { country_id: 1, state: 'Washington', abbreviation: 'WA' },
    { country_id: 1, state: 'West Virginia', abbreviation: 'WV' },
    { country_id: 1, state: 'Wisconsin', abbreviation: 'WI' },
    { country_id: 1, state: 'Wyoming', abbreviation: 'WY' },
    { country_id: 2, state: 'Alberta', abbreviation: 'AB' },
    { country_id: 2, state: 'British Columbia', abbreviation: 'BC' },
    { country_id: 2, state: 'Manitoba', abbreviation: 'MB' },
    { country_id: 2, state: 'New Brunswick', abbreviation: 'NB' },
    { country_id: 2, state: 'Newfoundland and Labrador', abbreviation: 'NL' },
    { country_id: 2, state: 'Northwest Territories', abbreviation: 'NT' },
    { country_id: 2, state: 'Nova Scotia', abbreviation: 'NS' },
    { country_id: 2, state: 'Nunavut', abbreviation: 'NU' },
    { country_id: 2, state: 'Ontario', abbreviation: 'ON' },
    { country_id: 2, state: 'Prince Edward Island', abbreviation: 'PE' },
    { country_id: 2, state: 'Quebec', abbreviation: 'QC' },
    { country_id: 2, state: 'Saskatchewan', abbreviation: 'SK' },
    { country_id: 2, state: 'Yukon', abbreviation: 'YT' },
    { country_id: 3, state: 'Aguascalientes', abbreviation: 'AG' },
    { country_id: 3, state: 'Baja California', abbreviation: 'BC' },
    { country_id: 3, state: 'Baja California Sur', abbreviation: 'BS' },
    { country_id: 3, state: 'Campeche', abbreviation: 'CM' },
    { country_id: 3, state: 'Chiapas', abbreviation: 'CS' },
    { country_id: 3, state: 'Chihuahua', abbreviation: 'CH' },
    { country_id: 3, state: 'Coahuila', abbreviation: 'CO' },
    { country_id: 3, state: 'Colima', abbreviation: 'CL' },
    { country_id: 3, state: 'Durango', abbreviation: 'DG' },
    { country_id: 3, state: 'Guanajuato', abbreviation: 'GT' },
    { country_id: 3, state: 'Guerrero', abbreviation: 'GR' },
    { country_id: 3, state: 'Hidalgo', abbreviation: 'HG' },
    { country_id: 3, state: 'Jalisco', abbreviation: 'JA' },
    { country_id: 3, state: 'Mexico', abbreviation: 'EM' },
    { country_id: 3, state: 'Michoacan', abbreviation: 'MI' },
    { country_id: 3, state: 'Morelos', abbreviation: 'MO' },
    { country_id: 3, state: 'Nayarit', abbreviation: 'NA' },
    { country_id: 3, state: 'Nuevo Leon', abbreviation: 'NL' },
    { country_id: 3, state: 'Oaxaca', abbreviation: 'OA' },
    { country_id: 3, state: 'Puebla', abbreviation: 'PU' },
    { country_id: 3, state: 'Queretaro', abbreviation: 'QT' },
    { country_id: 3, state: 'Quintana Roo', abbreviation: 'QR' },
    { country_id: 3, state: 'San Luis Potosi', abbreviation: 'SL' },
    { country_id: 3, state: 'Sinaloa', abbreviation: 'SI' },
    { country_id: 3, state: 'Sonora', abbreviation: 'SO' },
    { country_id: 3, state: 'Tabasco', abbreviation: 'TB' },
    { country_id: 3, state: 'Tamaulipas', abbreviation: 'TL' },
    { country_id: 3, state: 'Tlaxcala', abbreviation: 'TM' },
    { country_id: 3, state: 'Veracruz', abbreviation: 'VE' },
    { country_id: 3, state: 'Yucatan', abbreviation: 'YU' },
    { country_id: 3, state: 'Zacatecas', abbreviation: 'ZA' },
    { country_id: 4, state: 'Andhra Pradesh', abbreviation: 'AD' },
    { country_id: 4, state: 'Arunachal Pradesh', abbreviation: 'AR' },
    { country_id: 4, state: 'Assam', abbreviation: 'AS' },
    { country_id: 4, state: 'Bihar', abbreviation: 'BR' },
    { country_id: 4, state: 'Chattisgarh', abbreviation: 'CG' },
    { country_id: 4, state: 'Delhi', abbreviation: 'DL' },
    { country_id: 4, state: 'Goa', abbreviation: 'GA' },
    { country_id: 4, state: 'Gujarat', abbreviation: 'GJ' },
    { country_id: 4, state: 'Haryana', abbreviation: 'HR' },
    { country_id: 4, state: 'Himachal Pradesh', abbreviation: 'HP' },
    { country_id: 4, state: 'Jammu and Kashmir', abbreviation: 'JK' },
    { country_id: 4, state: 'Jharkhand', abbreviation: 'JH' },
    { country_id: 4, state: 'Karnataka', abbreviation: 'KA' },
    { country_id: 4, state: 'Kerala', abbreviation: 'KL' },
    { country_id: 4, state: 'Lakshadweep Islands', abbreviation: 'LD' },
    { country_id: 4, state: 'Madhya Pradesh', abbreviation: 'MP' },
    { country_id: 4, state: 'Maharashtra', abbreviation: 'MH' },
    { country_id: 4, state: 'Manipur', abbreviation: 'MN' },
    { country_id: 4, state: 'Meghalaya', abbreviation: 'ML' },
    { country_id: 4, state: 'Mizoram', abbreviation: 'MZ' },
    { country_id: 4, state: 'Nagaland', abbreviation: 'NL' },
    { country_id: 4, state: 'Odisha', abbreviation: 'OD' },
    { country_id: 4, state: 'Pondicherry', abbreviation: 'PY' },
    { country_id: 4, state: 'Punjab', abbreviation: 'PB' },
    { country_id: 4, state: 'Rajasthan', abbreviation: 'RJ' },
    { country_id: 4, state: 'Sikkim', abbreviation: 'SK' },
    { country_id: 4, state: 'Tamil Nadu', abbreviation: 'TN' },
    { country_id: 4, state: 'Telangana', abbreviation: 'TS' },
    { country_id: 5, state: 'New South Wales', abbreviation: 'xx' },
    { country_id: 5, state: 'Queensland', abbreviation: 'xx' },
    { country_id: 5, state: 'South Australia', abbreviation: 'xx' },
    { country_id: 5, state: 'Tasmania', abbreviation: 'xx' },
    { country_id: 5, state: 'Victoria', abbreviation: 'xx' },
    { country_id: 5, state: 'Western Australia', abbreviation: 'xx' },
];

export const SELECT_CUSTOM_STYLE = (nonDisabledColor: string, notHaveValueColor: string|null = null, disabledColor: string = '#fff') => ({
    option: (provided: any) => ({
        ...provided,
        whiteSpace: 'normal', // Allow text to wrap to the next line
        wordWrap: 'break-word', // Break long words
    }),
    menu: (provided: any) => ({
        ...provided,
        width: 'auto', // Adjust the width of the menu to fit the content
        minWidth: '100%', // Ensure the menu is at least as wide as the select input
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        backgroundColor: state.isDisabled
            ? disabledColor : notHaveValueColor ? (state.hasValue ? nonDisabledColor : notHaveValueColor) : nonDisabledColor
    }),
    singleValue: (provided: any) => ({
        ...provided,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? 'hsl(0, 0%, 50%)' : 'black',
    }),
});
